import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, DatePicker, Modal, Spin, Table, Tag, message } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import HighchartsReact from "highcharts-react-official";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { getUserAnalyticsDashboard } from "../../../apis/analyticsUsageApi";
import AnalyticsUserEventsModal, {
  DEFAULT_FILTERS,
  EventFilters,
  buildFilterOptions,
  filterEvents,
  formatEventDetails,
} from "./AnalyticsUserEventsModal";
import AnalyticsPanelHead from "./AnalyticsPanelHead";
import AnalyticsHelpIcon from "./AnalyticsHelpIcon";
import { analyticsModalOverlayProps } from "./analyticsModalUtils";
import { ANALYTICS_DATE_PICKER_CLASS } from "./analyticsTheme";
import { buildUserFunnelChartSteps } from "./funnelHelpers";
import {
  donutOptions,
  exportEventsCsv,
  formatCurrency,
  formatDurationShort,
  formatNumber,
  funnelChartOptions,
  geoMapBubbleOptions,
  getEventPlatform,
  getEventVersion,
  googleMapsEmbedUrl,
  googleMapsSearchUrl,
  lineChartOptions,
  sparklineOptions,
  THEME,
} from "./chartHelpers";

dayjs.extend(relativeTime);

const { RangePicker } = DatePicker;
const MAX_RANGE_DAYS = 90;
const TIMELINE_PREVIEW = 8;
const SUBSCRIPTION_PREVIEW = 5;

const defaultRange = () => [
  dayjs().subtract(29, "day").startOf("day"),
  dayjs().endOf("day"),
];

const formatEventDetailsInline = formatEventDetails;

const ViewMoreBtn = ({ onClick, label = "View more" }) => (
  <button type="button" className="analytics-view-more" onClick={onClick} aria-label={label}>
    <span>{label}</span>
    <i className="fa-solid fa-arrow-right" />
  </button>
);

const computeInvestmentSummary = (subscriptions, events) => {
  const parseAmount = (value) => {
    if (value == null || value === "") return 0;
    const n = Number(String(value).replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  };

  const subscriptionRows = (subscriptions || []).map((s) => ({
    id: s.id,
    seq_no: s.seq_no,
    name: s.name_of_subscriber || s.share_class_type || `Subscription #${s.seq_no || s.id}`,
    share_class: s.share_class_type || "—",
    amount: parseAmount(s.current_amount ?? s.investment_amount),
    status: s.subscription_status || s.crm_sub_status || "—",
  }));

  const crmTotal = subscriptionRows.reduce((sum, row) => sum + row.amount, 0);

  const submissions = (events || []).filter((e) => e.event === "investment_submitted");
  const submittedRows = submissions.map((ev, i) => {
    const p = ev.properties || {};
    return {
      key: i,
      timestamp: ev.timestamp,
      amount: parseAmount(p.inv_amount ?? p.amount),
      share_class: p.share_class || "—",
      product: p.product_name || p.product_id || "—",
    };
  });
  const submittedInPeriod = submittedRows.reduce((sum, row) => sum + row.amount, 0);

  const amountChanges = (events || []).filter((e) => e.event === "investment_amount_changed");
  const maxIntentAmount = amountChanges.reduce((max, ev) => {
    const amount = parseAmount(ev.properties?.amount);
    return amount > max ? amount : max;
  }, 0);

  return {
    crmTotal,
    submittedInPeriod,
    submissionCount: submissions.length,
    subscriptionCount: subscriptionRows.length,
    subscriptionRows,
    submittedRows,
    maxIntentAmount: maxIntentAmount || null,
    flowStarted: (events || []).filter((e) => e.event === "investment_flow_started").length,
  };
};

const computeUserMetrics = (data) => {
  const events = data?.events || data?.recent_events || [];
  const sessionIds = new Set();
  let firstSeen = null;
  let lastActive = null;
  let screensViewed = 0;
  let conversionActions = 0;
  const conversionEvents = new Set([
    "signup_completed",
    "login_completed",
    "investment_submitted",
    "cta_clicked",
  ]);
  const sessionSpans = new Map();

  events.forEach((ev) => {
    const ts = ev.timestamp ? dayjs(ev.timestamp) : null;
    if (ts?.isValid()) {
      if (!firstSeen || ts.isBefore(firstSeen)) firstSeen = ts;
      if (!lastActive || ts.isAfter(lastActive)) lastActive = ts;
    }
    const sid = ev.properties?.session_id || ev.properties?.$session_id;
    if (sid) {
      sessionIds.add(sid);
      if (ts?.isValid()) {
        const span = sessionSpans.get(sid) || { min: ts, max: ts };
        if (ts.isBefore(span.min)) span.min = ts;
        if (ts.isAfter(span.max)) span.max = ts;
        sessionSpans.set(sid, span);
      }
    }
    if (ev.event === "page_view") screensViewed += 1;
    if (conversionEvents.has(ev.event)) conversionActions += 1;
  });

  const sessionDurations = Array.from(sessionSpans.values())
    .map(({ min, max }) => max.diff(min, "second"))
    .filter((s) => s >= 0);
  const avgSessionSec =
    sessionDurations.length > 0
      ? sessionDurations.reduce((a, b) => a + b, 0) / sessionDurations.length
      : null;

  return {
    sessions: sessionIds.size || (events.length ? 1 : 0),
    firstSeen,
    lastActive,
    screensViewed,
    conversionActions,
    avgSessionSec,
  };
};

const computeHealthScore = (data) => {
  const milestones = data?.activity?.milestones || [];
  const reached = milestones.filter((m) => m.reached).length;
  const errors = data?.activity?.categories?.errors || 0;
  const totalEvents = data?.summary?.total_events || 0;
  let score = 40 + reached * 10 - Math.min(errors * 3, 25);
  if (totalEvents > 15) score += 10;
  if (totalEvents > 40) score += 10;
  if (data?.activity?.flags?.investment_abandoned) score -= 8;
  if (data?.activity?.flags?.signup_abandoned) score -= 5;
  return Math.min(100, Math.max(0, Math.round(score)));
};

const generateInsights = (data, metrics) => {
  const insights = [];
  const flags = data?.activity?.flags || {};
  const milestones = data?.activity?.milestones || [];
  const categories = data?.activity?.categories || {};

  if (milestones.find((m) => m.key === "investment_submitted" && m.reached)) {
    insights.push("User completed the full investment funnel successfully.");
  }
  if ((metrics.investmentSummary?.crmTotal || 0) > 0) {
    insights.push(
      `Total CRM investment: ${formatCurrency(metrics.investmentSummary.crmTotal)} across ${metrics.investmentSummary.subscriptionCount} subscription(s).`
    );
  }
  if (milestones.find((m) => m.key === "signup_completed" && m.reached)) {
    insights.push("Signup journey completed — user is fully registered.");
  }
  if (categories.investment > 0 && !flags.investment_abandoned) {
    insights.push("Strong investment intent with no recorded abandon events.");
  }
  if (categories.navigation > 20) {
    insights.push("High navigation activity — user explores multiple screens.");
  }
  if (flags.signup_abandoned) {
    insights.push("Signup flow was abandoned — consider re-engagement outreach.");
  }
  if (flags.investment_abandoned) {
    insights.push("Investment flow abandoned — review drop-off step in funnel.");
  }
  if ((categories.errors || 0) > 0) {
    insights.push(`${categories.errors} error event(s) detected — check Errors & API section.`);
  }
  if (metrics.lastActive && dayjs().diff(metrics.lastActive, "day") <= 7) {
    insights.push("User was active within the last 7 days.");
  } else if (metrics.lastActive) {
    insights.push(`Last seen ${metrics.lastActive.format("D MMM YYYY")} — may need re-activation.`);
  }
  if (!insights.length) {
    insights.push("Limited activity in this period — try expanding the date range.");
  }
  return insights.slice(0, 5);
};

const aggregateProducts = (events) => {
  const map = new Map();
  events.forEach((ev) => {
    if (!["product_view", "product_details_viewed", "cta_clicked"].includes(ev.event)) return;
    const p = ev.properties || {};
    const name = p.product_name || p.product_id || p.product || "Unknown product";
    if (!map.has(name)) map.set(name, { product: name, views: 0, cta: 0 });
    const row = map.get(name);
    if (ev.event === "cta_clicked") row.cta += 1;
    else row.views += 1;
  });
  return Array.from(map.values()).sort((a, b) => b.views + b.cta - (a.views + a.cta));
};

const aggregateErrors = (events) =>
  events
    .filter((ev) =>
      ["js_error", "api_failed", "signup_failed", "investment_submit_failed"].includes(ev.event)
    )
    .slice(0, 12)
    .map((ev, i) => ({
      key: i,
      message:
        ev.properties?.error_message ||
        ev.properties?.message ||
        ev.properties?.reason ||
        ev.event,
      severity: ev.event === "js_error" ? "High" : "Medium",
      timestamp: ev.timestamp,
      event: ev.event,
    }));

const MilestoneTracker = ({ milestones }) => {
  const items = milestones || [];
  const reachedCount = items.filter((m) => m.reached).length;
  const pct = items.length ? (reachedCount / items.length) * 100 : 0;

  return (
    <div className="analytics-user360-milestones">
      <div className="analytics-user360-milestones__track">
        <div className="analytics-user360-milestones__fill" style={{ width: `${pct}%` }} />
        {items.map((m) => (
          <div
            key={m.key}
            className={`analytics-user360-milestones__step ${m.reached ? "reached" : "pending"}`}
            title={m.label}
          >
            <i className={`fa-solid fa-${m.reached ? "circle-check" : "circle"}`} />
            <span>{m.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HealthGauge = ({ score }) => {
  const label =
    score >= 75 ? "High Engagement" : score >= 45 ? "Moderate Engagement" : "Low Engagement";
  const color = score >= 75 ? THEME.primary : score >= 45 ? THEME.warning : THEME.purple;

  return (
    <div className="analytics-user360-health">
      <div
        className="analytics-user360-health__ring"
        style={{
          background: `conic-gradient(${color} ${score * 3.6}deg, #C2DAE1 ${score * 3.6}deg)`,
        }}
      >
        <div className="analytics-user360-health__inner">
          <strong>{score}</strong>
          <span>/ 100</span>
        </div>
      </div>
      <p className="analytics-user360-health__label">{label}</p>
    </div>
  );
};

const AnalyticsUserDashboard = () => {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const posthogDistinctId = searchParams.get("posthog_distinct_id") || undefined;
  const [dateRange, setDateRange] = useState(defaultRange);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [listModal, setListModal] = useState(null);
  const [eventFilters, setEventFilters] = useState(DEFAULT_FILTERS);
  const [showAllSubscriptions, setShowAllSubscriptions] = useState(false);

  const params = useMemo(
    () => ({
      from: dateRange[0].format("YYYY-MM-DD"),
      to: dateRange[1].format("YYYY-MM-DD"),
      limit: 500,
      ...(posthogDistinctId ? { posthog_distinct_id: posthogDistinctId } : {}),
    }),
    [dateRange, posthogDistinctId]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getUserAnalyticsDashboard(userId, params);
      setData(res?.result || null);
    } catch (e) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to load user analytics"
      );
    } finally {
      setLoading(false);
    }
  }, [userId, params]);

  useEffect(() => {
    load();
  }, [load]);

  const allEvents = useMemo(
    () => data?.events || data?.recent_events || [],
    [data]
  );
  const eventFilterOptions = useMemo(() => buildFilterOptions(allEvents), [allEvents]);
  const filteredEvents = useMemo(
    () => filterEvents(allEvents, eventFilters),
    [allEvents, eventFilters]
  );
  const metrics = useMemo(() => {
    if (!data) return null;
    const investmentSummary = computeInvestmentSummary(data.subscriptions, allEvents);
    return { ...computeUserMetrics(data), investmentSummary };
  }, [data, allEvents]);
  const healthScore = useMemo(() => (data ? computeHealthScore(data) : 0), [data]);
  const insights = useMemo(
    () => (data && metrics ? generateInsights(data, metrics) : []),
    [data, metrics]
  );
  const products = useMemo(() => aggregateProducts(allEvents), [allEvents]);
  const errors = useMemo(() => aggregateErrors(allEvents), [allEvents]);

  const activityChart = useMemo(() => {
    const byDay = data?.charts?.events_by_day || [];
    const sessionsByDay = data?.charts?.sessions_by_day || [];
    return lineChartOptions({
      categories: byDay.map((d) => dayjs(d.date).format("MMM D")),
      yTitle: "Count",
      series: [
        { name: "Events", data: byDay.map((d) => d.count), color: THEME.primary },
        {
          name: "Sessions",
          data: sessionsByDay.map((d) => d.count),
          color: THEME.success,
          dashStyle: "ShortDot",
        },
      ],
    });
  }, [data]);

  const signupFunnelChart = useMemo(() => {
    const steps = buildUserFunnelChartSteps(data?.activity?.funnels?.signup);
    return funnelChartOptions(steps, { seriesName: "This user", light: true });
  }, [data]);

  const investmentFunnelChart = useMemo(() => {
    const steps = buildUserFunnelChartSteps(data?.activity?.funnels?.investment);
    return funnelChartOptions(steps, { seriesName: "This user", light: true });
  }, [data]);

  const platformDonut = useMemo(
    () =>
      donutOptions(
        (data?.charts?.platforms || []).map((item) => ({
          label: item.label || "unknown",
          count: item.count,
        })),
        "Platform"
      ),
    [data]
  );

  const mapChart = useMemo(
    () => geoMapBubbleOptions(data?.location?.map_points || []),
    [data]
  );

  const copyText = async (label, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(String(text));
      message.success(`${label} copied`);
    } catch {
      message.error("Copy failed");
    }
  };

  const exportEvents = () => {
    exportEventsCsv(filteredEvents, `user-${userId}-events.csv`);
  };

  const user = data?.user || {};
  const identity = data?.analytics_identity || {};
  const activity = data?.activity || {};
  const categories = activity.categories || {};
  const location = data?.location || {};
  const devices = data?.devices || {};
  const observedDevices = devices.observed || [];
  const primaryDevice = observedDevices[0];
  const subscriptions = data?.subscriptions || [];
  const isActiveInvestor = subscriptions.some(
    (s) =>
      String(s.subscription_status || "").toLowerCase() === "active" ||
      String(s.crm_sub_status || "").toLowerCase().includes("active")
  );

  const userName =
    [user.first_name, user.last_name].filter(Boolean).join(" ") || `User #${userId}`;
  const platform =
    primaryDevice?.platform ||
    (data?.charts?.platforms || [])[0]?.label ||
    identity.af_status ||
    "—";

  const topEvents = (data?.charts?.events_by_name || []).slice(0, 6);
  const topScreens = (data?.charts?.pages || []).slice(0, 6);
  const investment = metrics?.investmentSummary;

  const kpiCards = data
    ? [
        {
          label: "Total Events",
          value: formatNumber(data.summary?.total_events),
          sub: "in period",
          spark: (data.charts?.events_by_day || []).map((d) => d.count),
        },
        {
          label: "Sessions",
          value: formatNumber(metrics.sessions),
          sub: "unique sessions",
        },
        {
          label: "Event Types",
          value: formatNumber(data.summary?.unique_event_names),
          sub: "distinct names",
        },
        {
          label: "First Seen",
          value: metrics.firstSeen ? metrics.firstSeen.format("D MMM YY") : "—",
          sub: metrics.firstSeen ? metrics.firstSeen.format("HH:mm") : "",
        },
        {
          label: "Last Active",
          value: metrics.lastActive ? metrics.lastActive.format("D MMM YY") : "—",
          sub: metrics.lastActive ? metrics.lastActive.fromNow() : "",
        },
        {
          label: "Avg Session",
          value: metrics.avgSessionSec != null ? formatDurationShort(metrics.avgSessionSec, 2) : "—",
          sub: "duration",
        },
        {
          label: "Screens Viewed",
          value: formatNumber(metrics.screensViewed),
          sub: "page_view events",
        },
        {
          label: "Conversions",
          value: formatNumber(metrics.conversionActions),
          sub: "key actions",
        },
        {
          label: "Investment Sum",
          value: formatCurrency(investment?.crmTotal || 0),
          sub:
            investment?.subscriptionCount > 0
              ? `${investment.subscriptionCount} subscription(s)`
              : investment?.submittedInPeriod > 0
                ? `${formatCurrency(investment.submittedInPeriod)} submitted in period`
                : "CRM portfolio",
          success: (investment?.crmTotal || 0) > 0,
        },
        // {
        //   label: "Errors",
        //   value: formatNumber(categories.errors),
        //   sub: "error events",
        //   danger: (categories.errors || 0) > 0,
        // },
      ]
    : [];

  const timelinePreview = filteredEvents.slice(0, TIMELINE_PREVIEW);
  const totalEvents = data?.summary?.total_events || allEvents.length;
  const filteredEventCount = filteredEvents.length;
  const subscriptionRows = investment?.subscriptionRows || [];
  const visibleSubscriptions = showAllSubscriptions
    ? subscriptionRows
    : subscriptionRows.slice(0, SUBSCRIPTION_PREVIEW);
  const hasMoreSubscriptions = subscriptionRows.length > SUBSCRIPTION_PREVIEW;
  const locationCities = location.cities || [];
  const primaryMapEmbed = googleMapsEmbedUrl(location.primary?.lat, location.primary?.lng);
  const primaryMapsLink = googleMapsSearchUrl({
    lat: location.primary?.lat,
    lng: location.primary?.lng,
    label: [location.primary?.city, location.primary?.country].filter(Boolean).join(", "),
  });

  const eventColumns = [
    {
      title: "Time",
      dataIndex: "timestamp",
      key: "time",
      width: 130,
      render: (v) => (v ? dayjs(v).format("D MMM HH:mm:ss") : "—"),
    },
    {
      title: "Event",
      dataIndex: "event",
      key: "event",
      render: (v) => <code className="analytics-code-sm">{v}</code>,
    },
    {
      title: "Screen",
      key: "screen",
      width: 110,
      render: (_, row) => row.properties?.page_name || "—",
    },
    {
      title: "Version",
      key: "version",
      width: 80,
      render: (_, row) => getEventVersion(row.properties || {}) || "—",
    },
    {
      title: "Details",
      key: "details",
      ellipsis: true,
      render: (_, row) => formatEventDetailsInline(row),
    },
    {
      title: "Device",
      key: "device",
      width: 100,
      render: (_, row) => getEventPlatform(row.properties || {}) || "—",
    },
  ];

  const listModalContent = useMemo(() => {
    if (!listModal) return null;
    if (listModal === "events") {
      return {
        title: "Top events",
        rows: (data?.charts?.events_by_name || []).map((r, i) => ({ ...r, key: i })),
        columns: [
          { title: "Event", dataIndex: "event", key: "e" },
          { title: "Count", dataIndex: "count", key: "c", render: formatNumber },
        ],
      };
    }
    if (listModal === "screens") {
      return {
        title: "Top screens",
        rows: (data?.charts?.pages || []).map((r, i) => ({ ...r, key: i })),
        columns: [
          { title: "Screen", dataIndex: "label", key: "l" },
          { title: "Views", dataIndex: "count", key: "c", render: formatNumber },
        ],
      };
    }
    return null;
  }, [listModal, data]);

  return (
    <div className="analytics-dashboard analytics-user-dashboard analytics-user360">
      <header className="analytics-dashboard__header analytics-user360__header">
        <div>
          <Link to="/analytics-usage/users" className="analytics-back-link">
            <i className="fa-solid fa-arrow-left" /> Back to Users
          </Link>
          <h1>
            User 360 Analytics
            <AnalyticsHelpIcon helpKey="user360-kpis" />
          </h1>
          {data && (
            <div className="analytics-user360-profile">
              <div className="analytics-user360-profile__identity">
                <strong>{userName}</strong>
                <span>{user.email || "—"}</span>
                <span className="analytics-user360-profile__crm">CRM ID: {user.id || userId}</span>
              </div>
              <div className="analytics-user360-profile__tags">
                {isActiveInvestor && <Tag color="green">Active Investor</Tag>}
                <Tag color="blue">Active</Tag>
                <Tag>{identity.af_media_source || "Direct / unknown"}</Tag>
                <Tag>{platform}</Tag>
              </div>
            </div>
          )}
        </div>
        <div className="analytics-dashboard__controls">
          <RangePicker
            popupClassName={ANALYTICS_DATE_PICKER_CLASS}
            value={dateRange}
            onChange={(dates) => {
              if (!dates?.[0] || !dates?.[1]) return;
              if (dates[1].diff(dates[0], "day") > MAX_RANGE_DAYS) {
                setError(`Max range is ${MAX_RANGE_DAYS} days`);
                return;
              }
              setDateRange(dates);
            }}
            allowClear={false}
            disabledDate={(c) => c && c > dayjs().endOf("day")}
          />
          <Button type="primary" onClick={load} loading={loading}>
            Refresh
          </Button>
        </div>
      </header>

      {error && (
        <Alert type="error" message={error} closable onClose={() => setError(null)} showIcon />
      )}

      {loading && !data ? (
        <div className="analytics-dashboard__loading">
          <Spin size="large" tip="Loading user activity..." />
        </div>
      ) : data ? (
        <>
          <div className="analytics-kpi-grid analytics-user360-kpis">
            {kpiCards.map((kpi) => (
              <div
                key={kpi.label}
                className={`analytics-kpi-card analytics-kpi-card--compact ${kpi.danger ? "analytics-kpi-card--danger" : ""} ${kpi.success ? "analytics-kpi-card--success" : ""}`}
              >
                <div className="analytics-kpi-card__top">
                  <span className="analytics-kpi-card__label">{kpi.label}</span>
                  {kpi.spark?.length > 1 && (
                    <HighchartsReact
                      options={sparklineOptions(kpi.spark, kpi.danger ? THEME.danger : THEME.primary)}
                      containerProps={{ style: { height: 28, width: 72 } }}
                    />
                  )}
                </div>
                <div className="analytics-kpi-card__value">{kpi.value}</div>
                {kpi.sub && <div className="analytics-kpi-card__sub">{kpi.sub}</div>}
              </div>
            ))}
          </div>

          <div className="analytics-panel analytics-user360-milestones-panel">
            <AnalyticsPanelHead
              title="Key milestones"
              subtitle="User journey progress in selected period"
              helpKey="user360-milestones"
            />
            <MilestoneTracker milestones={activity.milestones} />
            {activity.flags?.last_abandon && (
              <Alert
                type="warning"
                showIcon
                className="analytics-user-abandon-alert"
                message={`Last abandon: ${activity.flags.last_abandon.event} at ${dayjs(activity.flags.last_abandon.timestamp).format("D MMM HH:mm")}`}
              />
            )}
          </div>

          <div className="analytics-user360-body">
            <div className="analytics-user360-main">
              <div className="analytics-panel">
                <AnalyticsPanelHead
                  title="Activity over time"
                  subtitle="Events and sessions by day"
                  helpKey="user360-activity"
                />
                <HighchartsReact options={activityChart} />
              </div>

              <div className="analytics-grid analytics-grid--2">
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Top events"
                    subtitle="Most frequent event names"
                    helpKey="user360-top-events"
                    extra={
                      <ViewMoreBtn
                        label="View all"
                        onClick={() => setListModal("events")}
                      />
                    }
                  />
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table"
                    columns={[
                      { title: "Event", dataIndex: "event", key: "e" },
                      { title: "Count", dataIndex: "count", key: "c", render: formatNumber, width: 80 },
                    ]}
                    dataSource={topEvents.map((r, i) => ({ ...r, key: i }))}
                    locale={{ emptyText: "No events" }}
                  />
                </div>
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Top screens"
                    subtitle="page_view breakdown"
                    helpKey="user360-top-screens"
                    extra={
                      <ViewMoreBtn
                        label="View all"
                        onClick={() => setListModal("screens")}
                      />
                    }
                  />
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table"
                    columns={[
                      { title: "Screen", dataIndex: "label", key: "l" },
                      { title: "Views", dataIndex: "count", key: "c", render: formatNumber, width: 80 },
                    ]}
                    dataSource={topScreens.map((r, i) => ({ ...r, key: i }))}
                    locale={{ emptyText: "No page views" }}
                  />
                </div>
              </div>

              <div className="analytics-grid analytics-grid--2">
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Device & app context"
                    subtitle="Observed from PostHog events"
                    helpKey="user360-device"
                  />
                  {primaryDevice ? (
                    <div className="analytics-user360-device-context">
                      <div className="analytics-user360-device-context__primary">
                        <i className="fa-solid fa-mobile-screen-button" />
                        <div>
                          <strong>{primaryDevice.device_name || primaryDevice.brand || "Device"}</strong>
                          <span>
                            {primaryDevice.os || primaryDevice.platform || "—"}
                            {primaryDevice.app_version ? ` · v${primaryDevice.app_version}` : ""}
                          </span>
                        </div>
                      </div>
                      {(data?.charts?.platforms || []).length > 0 && (
                        <div className="analytics-user360-device-context__chart">
                          <HighchartsReact options={platformDonut} />
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="analytics-empty">No device properties in this period</p>
                  )}
                </div>
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Location insights"
                    helpKey="user360-location"
                    subtitle={
                      location.totals?.cities > 1
                        ? `${formatNumber(location.totals.cities)} cities · ${formatNumber(location.totals.countries)} countries`
                        : location.primary
                          ? [location.primary.city, location.primary.country].filter(Boolean).join(", ") || "—"
                          : "No geo data in period"
                    }
                    extra={
                      primaryMapsLink ? (
                        <a
                          href={primaryMapsLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="analytics-view-more"
                        >
                          Open in Google Maps <i className="fa-solid fa-arrow-up-right-from-square" />
                        </a>
                      ) : null
                    }
                  />
                  {location.primary && (
                    <div className="analytics-user-location-meta">
                      {location.primary.lat != null && location.primary.lng != null && (
                        <span>
                          Coords<strong>{location.primary.lat}, {location.primary.lng}</strong>
                        </span>
                      )}
                      <span>
                        Geo events<strong>{formatNumber(location.totals?.geo_events)}</strong>
                      </span>
                    </div>
                  )}
                  {primaryMapEmbed && (
                    <div className="analytics-user-location-map">
                      <iframe
                        title="Primary location map"
                        src={primaryMapEmbed}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                  )}
                  {locationCities.length > 0 && (
                    <div className="analytics-user-location-cities">
                      <h5>
                        {locationCities.length > 1 ? "All observed locations" : "Observed location"}
                      </h5>
                      <ul>
                        {locationCities.slice(0, 8).map((cityRow) => {
                          const label = [cityRow.city, cityRow.country].filter(Boolean).join(", ") || "Unknown";
                          const mapsUrl = googleMapsSearchUrl({
                            lat: cityRow.lat,
                            lng: cityRow.lng,
                            label,
                          });
                          return (
                            <li key={`${cityRow.city}-${cityRow.country}`}>
                              <div>
                                <strong>{label}</strong>
                                <span>{formatNumber(cityRow.events)} events</span>
                              </div>
                              {mapsUrl && (
                                <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
                                  Map
                                </a>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      {locationCities.length > 8 && (
                        <p className="analytics-user-location-note">
                          +{locationCities.length - 8} more location(s) in this period
                        </p>
                      )}
                    </div>
                  )}
                  {(location.map_points || []).length > 1 && (
                    <p className="analytics-user-location-note">
                      Multiple coordinates detected — map shows the most recent primary location; list above includes all cities.
                    </p>
                  )}
                  {(location.map_points || []).length ? (
                    <HighchartsReact options={mapChart} />
                  ) : !locationCities.length ? (
                    <p className="analytics-empty">No geo coordinates in this period</p>
                  ) : null}
                </div>
              </div>

              <div className="analytics-panel analytics-user360-investment">
                <AnalyticsPanelHead
                  title="Investment summary"
                  subtitle="CRM subscriptions and PostHog submission events in selected period"
                  helpKey="user360-investment"
                />
                <div className="analytics-user360-investment__stats">
                  <div>
                    <span>CRM total invested</span>
                    <strong>{formatCurrency(investment?.crmTotal || 0)}</strong>
                    <small>{formatNumber(investment?.subscriptionCount || 0)} subscription(s)</small>
                  </div>
                  <div>
                    <span>Submitted in period</span>
                    <strong>{formatCurrency(investment?.submittedInPeriod || 0)}</strong>
                    <small>{formatNumber(investment?.submissionCount || 0)} investment_submitted event(s)</small>
                  </div>
                  <div>
                    <span>Max intent (period)</span>
                    <strong>
                      {investment?.maxIntentAmount
                        ? formatCurrency(investment.maxIntentAmount)
                        : "—"}
                    </strong>
                    <small>{formatNumber(investment?.flowStarted || 0)} flow(s) started</small>
                  </div>
                </div>
                <div className="analytics-grid analytics-grid--2 analytics-user360-investment__tables">
                  <div>
                    <h5>CRM subscriptions</h5>
                    {subscriptionRows.length ? (
                      <>
                        <Table
                          size="small"
                          pagination={false}
                          className="analytics-table"
                          columns={[
                            { title: "Seq", dataIndex: "seq_no", key: "seq", width: 60 },
                            { title: "Share class", dataIndex: "share_class", key: "sc", ellipsis: true },
                            {
                              title: "Amount",
                              dataIndex: "amount",
                              key: "amt",
                              width: 110,
                              render: (v) => formatCurrency(v),
                            },
                            { title: "Status", dataIndex: "status", key: "st", width: 100 },
                          ]}
                          dataSource={visibleSubscriptions.map((r) => ({ ...r, key: r.id }))}
                        />
                        {hasMoreSubscriptions && (
                          <button
                            type="button"
                            className="analytics-user360-subscriptions-toggle"
                            onClick={() => setShowAllSubscriptions((v) => !v)}
                          >
                            {showAllSubscriptions
                              ? "Show less"
                              : `Read more (${subscriptionRows.length - SUBSCRIPTION_PREVIEW} more)`}
                          </button>
                        )}
                      </>
                    ) : (
                      <p className="analytics-empty">No CRM subscriptions for this user</p>
                    )}
                  </div>
                  <div>
                    <h5>Submissions in period</h5>
                    {investment?.submittedRows?.length ? (
                      <Table
                        size="small"
                        pagination={false}
                        className="analytics-table"
                        columns={[
                          {
                            title: "Time",
                            dataIndex: "timestamp",
                            key: "t",
                            width: 120,
                            render: (v) => (v ? dayjs(v).format("D MMM HH:mm") : "—"),
                          },
                          {
                            title: "Amount",
                            dataIndex: "amount",
                            key: "a",
                            width: 110,
                            render: (v) => formatCurrency(v),
                          },
                          { title: "Share class", dataIndex: "share_class", key: "sc", ellipsis: true },
                          { title: "Product", dataIndex: "product", key: "p", ellipsis: true },
                        ]}
                        dataSource={investment.submittedRows}
                      />
                    ) : (
                      <p className="analytics-empty">No investment_submitted events in this period</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="analytics-grid analytics-grid--2">
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Product interest"
                    subtitle="Views and CTA clicks by product"
                    helpKey="user360-products"
                  />
                  {products.length ? (
                    <Table
                      size="small"
                      pagination={false}
                      className="analytics-table"
                      columns={[
                        { title: "Product", dataIndex: "product", key: "p", ellipsis: true },
                        { title: "Views", dataIndex: "views", key: "v", width: 70, render: formatNumber },
                        { title: "CTA", dataIndex: "cta", key: "c", width: 60, render: formatNumber },
                      ]}
                      dataSource={products.slice(0, 8).map((r, i) => ({ ...r, key: i }))}
                    />
                  ) : (
                    <p className="analytics-empty">No product view events</p>
                  )}
                </div>
                <div className="analytics-panel">
                  <AnalyticsPanelHead
                    title="Journey funnels"
                    subtitle="This user's signup and investment progress (1 = reached, 0 = not reached)"
                    helpKey="user360-funnels"
                  />
                  <div className="analytics-user360-funnels">
                    <div>
                      <h5>Signup journey</h5>
                      <HighchartsReact options={signupFunnelChart} />
                    </div>
                    <div>
                      <h5>Investment journey</h5>
                      <HighchartsReact options={investmentFunnelChart} />
                    </div>
                  </div>
                </div>
              </div>

              <div className="analytics-panel" style={{ display: "none" }}>
                <AnalyticsPanelHead
                  title="Errors & API"
                  subtitle="Recent error and failure events"
                  helpKey="user360-errors"
                />
                {errors.length ? (
                  <Table
                    size="small"
                    pagination={false}
                    className="analytics-table"
                    columns={[
                      { title: "Error", dataIndex: "message", key: "m", ellipsis: true },
                      {
                        title: "Severity",
                        dataIndex: "severity",
                        key: "s",
                        width: 90,
                        render: (v) => (
                          <Tag color={v === "High" ? "red" : "orange"}>{v}</Tag>
                        ),
                      },
                      {
                        title: "Time",
                        dataIndex: "timestamp",
                        key: "t",
                        width: 120,
                        render: (v) => (v ? dayjs(v).format("D MMM HH:mm") : "—"),
                      },
                    ]}
                    dataSource={errors}
                  />
                ) : (
                  <p className="analytics-empty">No errors recorded in this period</p>
                )}
              </div>
            </div>

            <aside className="analytics-user360-sidebar">
              <div className="analytics-panel analytics-user360-health-panel">
                <AnalyticsPanelHead
                  title="User health score"
                  subtitle="Engagement in selected period"
                  helpKey="user360-health"
                />
                <HealthGauge score={healthScore} />
              </div>

              <div className="analytics-panel">
                <div className="analytics-panel__head">
                  <h4>Admin quick tools</h4>
                </div>
                <div className="analytics-user360-tools">
                  <Button size="small" onClick={exportEvents} disabled={!filteredEvents.length}>
                    <i className="fa-solid fa-download" /> Export CSV
                  </Button>
                  <Button size="small" onClick={() => copyText("User ID", user.id || userId)}>
                    <i className="fa-solid fa-copy" /> Copy user ID
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      copyText("PostHog ID", data.distinct_id || identity.posthog_distinct_id)
                    }
                  >
                    <i className="fa-solid fa-copy" /> Copy PostHog ID
                  </Button>
                  <Button
                    size="small"
                    onClick={() => copyText("AppsFlyer ID", identity.appsflyer_id)}
                    disabled={!identity.appsflyer_id}
                  >
                    <i className="fa-solid fa-copy" /> Copy AppsFlyer ID
                  </Button>
                </div>
              </div>

              <div className="analytics-panel">
                <AnalyticsPanelHead title="Retention & recency" helpKey="user360-retention" />
                <div className="analytics-mini-stats">
                  <div className="analytics-mini-stat">
                    <span>Last seen</span>
                    <strong>
                      {metrics.lastActive ? metrics.lastActive.format("D MMM YYYY") : "—"}
                    </strong>
                  </div>
                  <div className="analytics-mini-stat">
                    <span>Days since active</span>
                    <strong>
                      {metrics.lastActive ? dayjs().diff(metrics.lastActive, "day") : "—"}
                    </strong>
                  </div>
                  <div className="analytics-mini-stat">
                    <span>Active days</span>
                    <strong>{formatNumber(data.charts?.active_days)}</strong>
                  </div>
                  <div className="analytics-mini-stat">
                    <span>PostHog distinct ID</span>
                    <strong className="analytics-user360-id">
                      {(data.distinct_id || identity.posthog_distinct_id || "—").slice(0, 18)}
                      {(data.distinct_id || identity.posthog_distinct_id || "").length > 18 ? "…" : ""}
                    </strong>
                  </div>
                </div>
              </div>

              <div className="analytics-panel">
                <AnalyticsPanelHead title="Insights" helpKey="user360-insights" />
                <ul className="analytics-user360-insights">
                  {insights.map((text) => (
                    <li key={text}>
                      <i className="fa-solid fa-lightbulb" />
                      {text}
                    </li>
                  ))}
                </ul>
              </div>
            </aside>
          </div>

          <div className="analytics-panel analytics-user360-timeline">
            <AnalyticsPanelHead
              title="Event timeline"
              helpKey="user360-timeline"
              subtitle={
                filteredEventCount === totalEvents
                  ? `${formatNumber(totalEvents)} events — newest first`
                  : `${formatNumber(filteredEventCount)} of ${formatNumber(totalEvents)} events — filtered`
              }
              extra={
                <ViewMoreBtn
                  label={`View all events (${formatNumber(filteredEventCount)})`}
                  onClick={() => setEventsModalOpen(true)}
                />
              }
            />
            <EventFilters
              filters={eventFilters}
              onChange={setEventFilters}
              options={eventFilterOptions}
              compact
            />
            <Table
              size="small"
              className="analytics-table"
              columns={eventColumns}
              dataSource={timelinePreview.map((r, i) => ({ ...r, key: `${r.timestamp}-${i}` }))}
              pagination={false}
              locale={{ emptyText: "No events match your filters" }}
            />
            {filteredEventCount > TIMELINE_PREVIEW && (
              <button
                type="button"
                className="analytics-user360-timeline__more"
                onClick={() => setEventsModalOpen(true)}
              >
                View all events ({formatNumber(filteredEventCount)})
              </button>
            )}
          </div>

          <AnalyticsUserEventsModal
            open={eventsModalOpen}
            onClose={() => setEventsModalOpen(false)}
            events={allEvents}
            userName={userName}
            userId={userId}
            initialFilters={eventFilters}
          />

          <Modal
            open={!!listModal}
            title={listModalContent?.title}
            onCancel={() => setListModal(null)}
            footer={null}
            width={640}
            destroyOnClose
            className="analytics-detail-modal"
            {...analyticsModalOverlayProps}
            classNames={{ body: "analytics-detail-modal__body" }}
          >
            {listModalContent && (
              <Table
                size="small"
                className="analytics-table"
                columns={listModalContent.columns}
                dataSource={listModalContent.rows}
                pagination={{ pageSize: 20, size: "small" }}
              />
            )}
          </Modal>
        </>
      ) : null}
    </div>
  );
};

export default AnalyticsUserDashboard;
